import React, { useState } from 'react';
import { Star, Wrench, Shield, MapPin } from '../lib/icons';
import SEOHead from '../components/SEOHead';
import {
  SOCIAL_PLATFORMS,
  GOOGLE_BUSINESS,
  FEATURED_VIDEOS,
  TRANSFORMATION_PHOTOS,
  TRANSFORMATION_CATEGORIES,
  type TransformationCategory,
} from '../data/social-config';

// ── Inline SVG brand icons ────────────────────────────────────────────────────

const YouTubeIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z" />
  </svg>
);

const InstagramIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const FacebookIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const GoogleIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// ── Platform card config ──────────────────────────────────────────────────────

const PLATFORM_CARDS = [
  {
    key: 'youtube' as const,
    label: 'YouTube',
    Icon: YouTubeIcon,
    color: '#FF0000',
    bgColor: '#FF000015',
    borderColor: '#FF000040',
  },
  {
    key: 'tiktok' as const,
    label: 'TikTok',
    Icon: TikTokIcon,
    color: '#E0E0E0',
    bgColor: '#FFFFFF10',
    borderColor: '#FFFFFF25',
  },
  {
    key: 'instagram' as const,
    label: 'Instagram',
    Icon: InstagramIcon,
    color: '#E1306C',
    bgColor: '#E1306C15',
    borderColor: '#E1306C40',
  },
  {
    key: 'facebook' as const,
    label: 'Facebook',
    Icon: FacebookIcon,
    color: '#1877F2',
    bgColor: '#1877F215',
    borderColor: '#1877F240',
  },
] as const;

const PLACEHOLDER_VIDEO_TITLES = [
  'PS5 HDMI Port Replacement — Full Walkthrough',
  'Custom DualSense Build: TMR Hall-Effect Upgrade',
  'Nintendo Switch USB-C Board Repair',
  'Steam Deck SSD Upgrade Start to Finish',
  'PS5 Deep Clean: Before & After',
  '3DS Speaker Flex & LCD Backlight Fix',
];

const PLACEHOLDER_TRANSFORM_ITEMS: { category: TransformationCategory; label: string }[] = [
  { category: 'playstation', label: 'PS5 Deep Clean' },
  { category: 'playstation', label: 'PS5 HDMI Repair' },
  { category: 'controllers', label: 'Custom DualSense Build' },
  { category: 'nintendo',    label: 'Switch USB-C Repair' },
  { category: 'steam-deck',  label: 'Steam Deck SSD Upgrade' },
  { category: 'controllers', label: 'SCUF Paddle Install' },
  { category: 'xbox',        label: 'Xbox Series X Repair' },
  { category: 'nintendo',    label: '3DS LCD Backlight Fix' },
  { category: 'computers',   label: 'Gaming PC Thermal Paste' },
];

// ── Page ──────────────────────────────────────────────────────────────────────

const CommunityPage = () => {
  const [activeCategory, setActiveCategory] = useState<TransformationCategory>('all');

  const hasRealPhotos = TRANSFORMATION_PHOTOS.length > 0;
  const hasRealVideos = FEATURED_VIDEOS.length > 0;

  const displayedPhotos = hasRealPhotos
    ? (activeCategory === 'all'
        ? TRANSFORMATION_PHOTOS
        : TRANSFORMATION_PHOTOS.filter(p => p.category === activeCategory))
    : PLACEHOLDER_TRANSFORM_ITEMS.filter(
        p => activeCategory === 'all' || p.category === activeCategory,
      );

  const displayedVideos = hasRealVideos
    ? FEATURED_VIDEOS.slice(0, 6)
    : PLACEHOLDER_VIDEO_TITLES.slice(0, 6);

  const jsonLD = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'GameFix Solutions',
    url: 'https://gamefixsolutions.com',
    sameAs: [
      SOCIAL_PLATFORMS.youtube.url,
      SOCIAL_PLATFORMS.tiktok.url,
      SOCIAL_PLATFORMS.instagram.url,
      SOCIAL_PLATFORMS.facebook.url,
    ].filter(Boolean),
  };

  return (
    <div className="bg-[#0D0D0D] min-h-screen">
      <SEOHead
        title="Community & Real Repairs | GameFix Solutions"
        description="Watch real PS5, Xbox, Nintendo Switch, and controller repairs every week on YouTube and TikTok. See before-and-after transformations and join thousands of happy customers."
        keywords="gamefix solutions youtube, ps5 repair videos, controller repair tiktok, gaming repair community"
        canonical="https://gamefixsolutions.com/community"
        jsonLD={jsonLD}
      />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative bg-gradient-to-b from-black via-[#0D0D0D] to-[#0D0D0D] py-20 overflow-hidden border-b border-gray-900">
        <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSIjMzlGRjE0IiBzdHJva2Utd2lkdGg9IjAuNSIvPjwvZz48L3N2Zz4=')]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-full px-5 py-1.5 mb-6">
            <span className="text-[#39FF14] font-semibold text-sm tracking-wide">COMMUNITY & SOCIAL PROOF</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#E0E0E0] mb-4">
            Watch Real Repairs Every Week
          </h1>
          <p className="text-xl text-[#B0B0B0] max-w-2xl mx-auto">
            We post real repairs, custom controller builds, cleaning transformations, upgrades, and
            behind-the-scenes videos from our shop.
          </p>
        </div>
      </section>

      {/* ── Section 1: Follow Our Repairs ─────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-2xl md:text-3xl font-bold text-[#E0E0E0] mb-2">Follow Our Repairs</h2>
        <p className="text-[#B0B0B0] mb-10 max-w-xl">
          Pick your platform and come hang. New content drops every week.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PLATFORM_CARDS.map(({ key, label, Icon, color, bgColor, borderColor }) => {
            const platform = SOCIAL_PLATFORMS[key];
            const hasUrl = !!platform.url;
            const Wrapper = hasUrl ? 'a' : 'div';
            const wrapperProps = hasUrl
              ? { href: platform.url, target: '_blank', rel: 'noopener noreferrer' }
              : {};

            return (
              <Wrapper
                key={key}
                {...(wrapperProps as any)}
                className={`group flex flex-col bg-gray-900 rounded-2xl border p-6 transition-all duration-300 ${
                  hasUrl ? 'hover:scale-[1.02] cursor-pointer' : 'opacity-70 cursor-default'
                }`}
                style={{ borderColor }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: bgColor }}
                >
                  <Icon className="h-6 w-6" style={{ color }} />
                </div>

                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-[#E0E0E0] font-bold text-lg">{label}</h3>
                  {platform.handle && (
                    <span className="text-xs text-[#B0B0B0]">{platform.handle}</span>
                  )}
                </div>

                {/* Stat row — visible only when counts are populated */}
                {(platform.followerCount !== null || platform.videoCount !== null) && (
                  <div className="flex gap-4 mb-3 text-sm">
                    {platform.followerCount !== null && (
                      <span className="text-[#39FF14] font-semibold">
                        {platform.followerCount.toLocaleString()} followers
                      </span>
                    )}
                    {platform.videoCount !== null && (
                      <span className="text-[#B0B0B0]">
                        {platform.videoCount.toLocaleString()} videos
                      </span>
                    )}
                  </div>
                )}

                <p className="text-[#B0B0B0] text-sm leading-relaxed flex-1 mb-5">
                  {platform.description}
                </p>

                <span
                  className="inline-flex items-center justify-center w-full py-2 px-4 rounded-lg text-sm font-bold transition-all duration-200"
                  style={{
                    backgroundColor: hasUrl ? `${color}20` : '#FFFFFF10',
                    color: hasUrl ? color : '#606060',
                    border: `1px solid ${hasUrl ? color + '50' : '#FFFFFF15'}`,
                  }}
                >
                  {hasUrl ? platform.cta : 'Coming Soon'}
                </span>
              </Wrapper>
            );
          })}
        </div>
      </section>

      {/* ── Section 2: Recent Videos ──────────────────────────────────────── */}
      <section className="bg-gray-950 border-y border-gray-800 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#E0E0E0] mb-2">Recent Videos</h2>
              <p className="text-[#B0B0B0]">
                {hasRealVideos
                  ? 'Our latest uploads — watch the full repair process.'
                  : 'Video library coming soon — subscribe so you don\'t miss a drop.'}
              </p>
            </div>
            <a
              href={SOCIAL_PLATFORMS.youtube.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#FF0000] hover:bg-[#CC0000] text-white font-bold px-5 py-2.5 rounded-lg transition-colors text-sm whitespace-nowrap"
            >
              <YouTubeIcon className="h-4 w-4" />
              Subscribe on YouTube
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {hasRealVideos
              ? (displayedVideos as typeof FEATURED_VIDEOS).map(video => (
                  <a
                    key={video.id}
                    href={video.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-gray-900 rounded-2xl border border-gray-800 hover:border-[#FF0000]/50 overflow-hidden transition-all duration-300"
                  >
                    <div className="relative aspect-video bg-gray-800 overflow-hidden">
                      <img
                        src={video.thumbnailUrl}
                        alt={video.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded">
                        {video.duration}
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="bg-[#FF0000] rounded-full p-3">
                          <svg viewBox="0 0 24 24" fill="white" className="h-6 w-6">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-[#E0E0E0] font-semibold text-sm leading-snug group-hover:text-[#39FF14] transition-colors">
                        {video.title}
                      </h3>
                    </div>
                  </a>
                ))
              : (displayedVideos as string[]).map((title, i) => (
                  <div
                    key={i}
                    className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden"
                  >
                    {/* Placeholder thumbnail */}
                    <div className="aspect-video bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center relative">
                      <div className="absolute inset-0 opacity-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSIjMzlGRjE0IiBzdHJva2Utd2lkdGg9IjAuNSIvPjwvZz48L3N2Zz4=')]" />
                      <div className="bg-[#FF0000]/20 rounded-full p-4 border border-[#FF0000]/30">
                        <YouTubeIcon className="h-8 w-8 text-[#FF0000]" />
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="h-4 bg-gray-700 rounded w-3/4 mb-2 animate-pulse" />
                      <div className="h-3 bg-gray-800 rounded w-1/2 animate-pulse" />
                      <p className="text-[#B0B0B0] text-xs mt-3 italic">{title}</p>
                    </div>
                  </div>
                ))}
          </div>
        </div>
      </section>

      {/* ── Section 3: Customer Transformations ───────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-2xl md:text-3xl font-bold text-[#E0E0E0] mb-2">
          Customer Transformations
        </h2>
        <p className="text-[#B0B0B0] mb-8 max-w-xl">
          Before and after photos from real repairs — dirty consoles cleaned, broken boards fixed,
          controllers built from scratch.
        </p>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-10">
          {TRANSFORMATION_CATEGORIES.map(cat => (
            <button
              key={cat.slug}
              onClick={() => setActiveCategory(cat.slug)}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                activeCategory === cat.slug
                  ? 'bg-[#39FF14] text-black border-[#39FF14]'
                  : 'text-[#B0B0B0] border-gray-700 hover:border-gray-500 hover:text-[#E0E0E0]'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Gallery grid */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5">
          {hasRealPhotos
            ? (displayedPhotos as typeof TRANSFORMATION_PHOTOS).map(photo => (
                <div
                  key={photo.id}
                  className="break-inside-avoid bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden"
                >
                  <img
                    src={photo.imageUrl}
                    alt={photo.title}
                    loading="lazy"
                    className="w-full object-cover"
                  />
                  <div className="p-4">
                    <span className="inline-block text-xs font-bold text-[#39FF14] border border-[#39FF14]/30 bg-[#39FF14]/10 px-2 py-0.5 rounded-full mb-2 capitalize">
                      {photo.category.replace('-', ' ')}
                    </span>
                    <h3 className="text-[#E0E0E0] font-semibold text-sm">{photo.title}</h3>
                    {photo.description && (
                      <p className="text-[#B0B0B0] text-xs mt-1 leading-relaxed">{photo.description}</p>
                    )}
                  </div>
                </div>
              ))
            : (displayedPhotos as typeof PLACEHOLDER_TRANSFORM_ITEMS).map((item, i) => (
                <div
                  key={i}
                  className="break-inside-avoid bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden"
                  style={{ height: `${180 + (i % 3) * 60}px` }}
                >
                  <div className="h-full flex flex-col items-center justify-center p-6 gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#39FF14]/10 border border-[#39FF14]/20 flex items-center justify-center">
                      <Wrench className="h-5 w-5 text-[#39FF14]" />
                    </div>
                    <span className="inline-block text-xs font-bold text-[#39FF14] border border-[#39FF14]/30 bg-[#39FF14]/10 px-2 py-0.5 rounded-full capitalize">
                      {item.category.replace('-', ' ')}
                    </span>
                    <p className="text-[#B0B0B0] text-sm text-center font-medium">{item.label}</p>
                    <p className="text-gray-600 text-xs text-center">Photo coming soon</p>
                  </div>
                </div>
              ))}
        </div>

        {displayedPhotos.length === 0 && (
          <div className="text-center py-16 text-[#B0B0B0]">
            No photos in this category yet — check back soon.
          </div>
        )}
      </section>

      {/* ── Section 4: Trust Badges ────────────────────────────────────────── */}
      <section className="bg-gray-950 border-y border-gray-800 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#E0E0E0] mb-2 text-center">
            Why Customers Trust GameFix
          </h2>
          <p className="text-[#B0B0B0] mb-12 text-center max-w-xl mx-auto">
            We've built a reputation on fast, honest repairs and work that actually holds up.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Reviews */}
            <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 text-center">
              <div className="flex justify-center gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-[#39FF14] fill-[#39FF14]" />
                ))}
              </div>
              <div className="text-3xl font-bold text-[#E0E0E0] mb-1">5.0</div>
              <p className="text-[#B0B0B0] text-sm">Thousands of successful repairs with top-rated reviews on Google.</p>
            </div>

            {/* Same-Day */}
            <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 text-center">
              <div className="w-12 h-12 bg-[#00BFFF]/10 rounded-xl flex items-center justify-center mx-auto mb-3 border border-[#00BFFF]/20">
                <svg viewBox="0 0 24 24" fill="none" stroke="#00BFFF" strokeWidth="2" className="h-6 w-6">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              </div>
              <div className="text-lg font-bold text-[#E0E0E0] mb-1">Same-Day Repairs</div>
              <p className="text-[#B0B0B0] text-sm">Many repairs completed in 3–4 hours. Drop off in the morning, pick up before dinner.</p>
            </div>

            {/* Warranty */}
            <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 text-center">
              <div className="w-12 h-12 bg-[#39FF14]/10 rounded-xl flex items-center justify-center mx-auto mb-3 border border-[#39FF14]/20">
                <Shield className="h-6 w-6 text-[#39FF14]" />
              </div>
              <div className="text-lg font-bold text-[#E0E0E0] mb-1">90-Day Warranty</div>
              <p className="text-[#B0B0B0] text-sm">Most repairs include our 90-day parts and labor warranty. We stand behind our work.</p>
            </div>

            {/* Local */}
            <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 text-center">
              <div className="w-12 h-12 bg-[#FF1744]/10 rounded-xl flex items-center justify-center mx-auto mb-3 border border-[#FF1744]/20">
                <MapPin className="h-6 w-6 text-[#FF1744]" />
              </div>
              <div className="text-lg font-bold text-[#E0E0E0] mb-1">Local Experts</div>
              <p className="text-[#B0B0B0] text-sm">Walk-in locations in San Diego and Temecula, or mail in from anywhere in the US.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 5: Connect With Us ─────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-[#E0E0E0] mb-3">Connect With Us</h2>
        <p className="text-[#B0B0B0] mb-10 max-w-md mx-auto">
          Follow along on every platform to see the latest repairs, builds, and behind-the-scenes content.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-4">
          {/* YouTube */}
          <a
            href={SOCIAL_PLATFORMS.youtube.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-gray-900 hover:bg-gray-800 border border-gray-700 hover:border-[#FF0000]/50 rounded-xl px-5 py-3 transition-all duration-200 group"
          >
            <YouTubeIcon className="h-5 w-5 text-[#FF0000]" />
            <div className="text-left">
              <div className="text-[#E0E0E0] font-semibold text-sm group-hover:text-[#FF0000] transition-colors">YouTube</div>
              <div className="text-[#B0B0B0] text-xs">{SOCIAL_PLATFORMS.youtube.handle || '@gamefixsolutions'}</div>
            </div>
          </a>

          {/* TikTok */}
          <a
            href={SOCIAL_PLATFORMS.tiktok.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-gray-900 hover:bg-gray-800 border border-gray-700 hover:border-white/30 rounded-xl px-5 py-3 transition-all duration-200 group"
          >
            <TikTokIcon className="h-5 w-5 text-[#E0E0E0]" />
            <div className="text-left">
              <div className="text-[#E0E0E0] font-semibold text-sm group-hover:text-white transition-colors">TikTok</div>
              <div className="text-[#B0B0B0] text-xs">{SOCIAL_PLATFORMS.tiktok.handle || '@gamefixsolutions'}</div>
            </div>
          </a>

          {/* Instagram */}
          {SOCIAL_PLATFORMS.instagram.url ? (
            <a
              href={SOCIAL_PLATFORMS.instagram.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-gray-900 hover:bg-gray-800 border border-gray-700 hover:border-[#E1306C]/50 rounded-xl px-5 py-3 transition-all duration-200 group"
            >
              <InstagramIcon className="h-5 w-5 text-[#E1306C]" />
              <div className="text-left">
                <div className="text-[#E0E0E0] font-semibold text-sm group-hover:text-[#E1306C] transition-colors">Instagram</div>
                <div className="text-[#B0B0B0] text-xs">{SOCIAL_PLATFORMS.instagram.handle}</div>
              </div>
            </a>
          ) : (
            <div className="flex items-center gap-3 bg-gray-900 border border-gray-800 rounded-xl px-5 py-3 opacity-50 cursor-default">
              <InstagramIcon className="h-5 w-5 text-[#E1306C]" />
              <div className="text-left">
                <div className="text-[#E0E0E0] font-semibold text-sm">Instagram</div>
                <div className="text-[#B0B0B0] text-xs">Coming soon</div>
              </div>
            </div>
          )}

          {/* Facebook */}
          {SOCIAL_PLATFORMS.facebook.url ? (
            <a
              href={SOCIAL_PLATFORMS.facebook.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-gray-900 hover:bg-gray-800 border border-gray-700 hover:border-[#1877F2]/50 rounded-xl px-5 py-3 transition-all duration-200 group"
            >
              <FacebookIcon className="h-5 w-5 text-[#1877F2]" />
              <div className="text-left">
                <div className="text-[#E0E0E0] font-semibold text-sm group-hover:text-[#1877F2] transition-colors">Facebook</div>
                <div className="text-[#B0B0B0] text-xs">{SOCIAL_PLATFORMS.facebook.handle}</div>
              </div>
            </a>
          ) : (
            <div className="flex items-center gap-3 bg-gray-900 border border-gray-800 rounded-xl px-5 py-3 opacity-50 cursor-default">
              <FacebookIcon className="h-5 w-5 text-[#1877F2]" />
              <div className="text-left">
                <div className="text-[#E0E0E0] font-semibold text-sm">Facebook</div>
                <div className="text-[#B0B0B0] text-xs">Coming soon</div>
              </div>
            </div>
          )}

          {/* Google — one button per location */}
          {GOOGLE_BUSINESS.locations.map(loc => (
            <a
              key={loc.label}
              href={loc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-gray-900 hover:bg-gray-800 border border-gray-700 hover:border-[#4285F4]/50 rounded-xl px-5 py-3 transition-all duration-200 group"
            >
              <GoogleIcon className="h-5 w-5" />
              <div className="text-left">
                <div className="text-[#E0E0E0] font-semibold text-sm group-hover:text-[#4285F4] transition-colors">Google — {loc.label}</div>
                <div className="text-[#B0B0B0] text-xs">Leave a review</div>
              </div>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
};

export default CommunityPage;
